import { buildLoadingHint, buildPageHeader, requestGoChat } from "./sheet-ui.js";
import { buildAudioPlayer } from "./audio-player.js";
/**
 * voice-v8 — Mediathek-View (Pre.157, inspiriert von voice.ssi.at v7).
 *
 * EINE View für ALLE Dateien (Bilder, PDFs, Audio, Texte, Andere) —
 * vorher waren Library (nur Bilder) und Files (PDFs/Texte) getrennt.
 *
 *  - Header: Titel + Suchfeld + Type-Filter-Tabs + Refresh
 *  - Type-Filter: Alle / Bilder / PDF / Audio / Andere (mime-basiert)
 *  - Body: Tiles im Grid für Bilder, kompakte Karten für andere Typen,
 *    gruppiert nach Datum (Heute/Gestern/Diese Woche/Datum)
 *  - Empty-States für leer/Filter/Suche
 *
 * Lädt /api/uploads?kind=all → bekommt alle hochgeladenen + KI-generierten
 * + PTT-aufgezeichneten Dateien. Filter passiert client-side.
 */

export interface UploadDto {
  id: string;
  url: string;
  mime: string;
  size: number;
  caption: string | null;
  source: "user" | "generated" | "edited" | "schmied";
  parentId: string | null;
  createdAt: string;
}

interface LibraryViewOptions {
  onAttach?: (item: UploadDto) => void;
}

type FilterKey = "all" | "image" | "pdf" | "audio" | "other";

const FILTERS: ReadonlyArray<{ id: FilterKey; label: string }> = [
  { id: "all", label: "Alle" },
  { id: "image", label: "Bilder" },
  { id: "pdf", label: "PDF" },
  { id: "audio", label: "Audio" },
  { id: "other", label: "Andere" },
];

function mimeType(mime: string): FilterKey {
  if (mime.startsWith("image/")) return "image";
  if (mime === "application/pdf") return "pdf";
  if (mime.startsWith("audio/") || mime.startsWith("video/webm"))
    return "audio";
  return "other";
}

export class LibraryView {
  private root: HTMLElement;
  private opts: LibraryViewOptions;
  private items: UploadDto[] = [];
  private filter: FilterKey = "all";
  private query = "";
  // 2026-07-14 (Martin, "Auswahl zwischen Liste und Cards, wie ueblich"):
  // Ansichts-Modus, gemerkt in localStorage.
  // 2026-07-14 (Martin, "Filter fuer Tage/Wochen, Timeline"): Zeitraum-
  // Filter + Monats-Stepper (bei "Monat" durch die Monate blaettern).
  private timeFilter: "alle" | "heute" | "woche" | "monat" = "alle";
  private monthCursor = 0; // 0 = aktueller Monat, -1 = Vormonat, ...
  private viewMode: "cards" | "list" =
    localStorage.getItem("voice-v8.library-view") === "list"
      ? "list"
      : "cards";
  private body!: HTMLElement;
  private filterRow!: HTMLElement;
  private searchInput!: HTMLInputElement;

  constructor(root: HTMLElement, opts: LibraryViewOptions = {}) {
    this.root = root;
    this.opts = opts;
    this.root.classList.add("library-view");
    this.renderShell();
    this.bindLightboxClose();
    void this.refresh();
  }

  async refresh(): Promise<void> {
    if (this.body && !this.body.hasChildNodes()) {
      this.body.replaceChildren(buildLoadingHint("Lade Mediathek \u2026"));
    }
    try {
      const res = await fetch("/api/uploads?limit=1000&kind=all", {
        credentials: "same-origin",
      });
      if (!res.ok) {
        this.items = [];
        this.renderBody(`Fehler ${res.status}`);
        return;
      }
      const data = (await res.json()) as { items: UploadDto[] };
      this.items = Array.isArray(data.items) ? data.items : [];
      this.renderFilterCounts();
      this.renderBody();
    } catch (err) {
      this.items = [];
      this.renderBody(
        `Konnte Bibliothek nicht laden: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }

  // --- Rendering -------------------------------------------------------

  private renderShell(): void {
    while (this.root.firstChild) this.root.removeChild(this.root.firstChild);

    const header = document.createElement("header");
    header.className = "library-header";

    // 2026-07-14 (Martin, "Mediathek wie ein Explorer"): Dateien DIREKT
    // hier hochladen (Bilder, PDFs, Audio, Texte — alles). Nutzt den
    // bestehenden /api/upload-Weg; die Liste laedt danach neu.
    const uploadBtn = document.createElement("button");
    uploadBtn.type = "button";
    uploadBtn.className = "library-refresh library-upload";
    uploadBtn.setAttribute("aria-label", "Dateien hochladen");
    uploadBtn.title = "Dateien hochladen";
    // 2026-07-24 (Martin, "nur ein Punkt"): Label dazu — die Kopf-Norm
    // stylt Aktionen als Text-Pille, ein nacktes Icon kollabierte.
    const upLabel = document.createElement("span");
    upLabel.textContent = "Hochladen";
    uploadBtn.appendChild(upLabel);
    uploadBtn.appendChild(
      makeSvgIcon(18, "M11 5h2v6h6v2h-6v6h-2v-6H5v-2h6z"),
    );
    const filePick = document.createElement("input");
    filePick.type = "file";
    filePick.multiple = true;
    filePick.hidden = true;
    uploadBtn.addEventListener("click", () => filePick.click());
    filePick.addEventListener("change", () => {
      const files = Array.from(filePick.files ?? []);
      filePick.value = "";
      if (!files.length) return;
      void (async () => {
        uploadBtn.disabled = true;
        uploadBtn.classList.add("is-busy");
        // Sichtbarer Fortschritt (Martin: "Upload-Symbol, damit ich sehe,
        // was da passiert"): Zeile oben im Body, X von N + Kreisel-Klasse.
        const prog = document.createElement("p");
        prog.className = "material-status library-upload-progress";
        this.body.prepend(prog);
        let done = 0;
        let fail = 0;
        for (const f of files) {
          done += 1;
          prog.textContent = `Lade hoch ${done} von ${files.length}: ${f.name}`;
          try {
            const form = new FormData();
            form.append("file", f);
            form.append("caption", f.name);
            const res = await fetch("/api/upload", {
              method: "POST",
              body: form,
              credentials: "same-origin",
            });
            if (!res.ok) fail += 1;
          } catch {
            fail += 1;
          }
        }
        uploadBtn.disabled = false;
        uploadBtn.classList.remove("is-busy");
        prog.remove();
        await this.refresh();
        if (fail > 0) {
          this.body.prepend(
            Object.assign(document.createElement("p"), {
              className: "library-upload-error",
              textContent: `${fail} Datei(en) konnten nicht hochgeladen werden.`,
            }),
          );
        }
      })();
    });
    // Liste <-> Cards
    const viewBtn = document.createElement("button");
    viewBtn.type = "button";
    viewBtn.className = "library-refresh library-viewmode";
    const paintViewBtn = (): void => {
      viewBtn.replaceChildren(
        makeSvgIcon(
          18,
          this.viewMode === "cards"
            ? "M3 5h18v2H3zm0 6h18v2H3zm0 6h18v2H3z"
            : "M4 4h7v7H4zm9 0h7v7h-7zM4 13h7v7H4zm9 0h7v7h-7z",
        ),
      );
      viewBtn.title =
        this.viewMode === "cards" ? "Als Liste zeigen" : "Als Karten zeigen";
      viewBtn.setAttribute("aria-label", viewBtn.title);
    };
    paintViewBtn();
    viewBtn.addEventListener("click", () => {
      this.viewMode = this.viewMode === "cards" ? "list" : "cards";
      try {
        localStorage.setItem("voice-v8.library-view", this.viewMode);
      } catch {
        /* localStorage gesperrt */
      }
      paintViewBtn();
      this.renderBody();
    });
    // NORM 2026-07-21: einheitlicher Kopf — Hochladen sichtbar,
    // Aktualisieren + Ansicht-Wechsel im ⋯-Menue, Pfeil -> Chat.
    header.appendChild(
      buildPageHeader("Mediathek", () => requestGoChat(), {
        reopenMenu: false,
        action: uploadBtn,
        menuItems: [
          { label: "Aktualisieren", onSelect: () => void this.refresh() },
          {
            label: "Ansicht: Liste/Karten wechseln",
            onSelect: () => viewBtn.click(),
          },
        ],
      }),
    );
    header.appendChild(filePick);

    // Suchfeld
    const searchWrap = document.createElement("div");
    searchWrap.className = "library-search";
    const searchIcon = document.createElement("span");
    searchIcon.className = "library-search-icon";
    searchIcon.appendChild(
      makeSvgIcon(
        16,
        "M15.5 14h-.79l-.28-.27a6.5 6.5 0 1 0-.7.7l.27.28v.79l5 5 1.49-1.49zM10 14a4 4 0 1 1 0-8 4 4 0 0 1 0 8z",
      ),
    );
    searchWrap.appendChild(searchIcon);
    this.searchInput = document.createElement("input");
    this.searchInput.type = "search";
    this.searchInput.className = "library-search-input";
    this.searchInput.placeholder = "Suchen …";
    this.searchInput.addEventListener("input", () => {
      this.query = this.searchInput.value.trim().toLowerCase();
      this.renderBody();
    });
    searchWrap.appendChild(this.searchInput);
    header.appendChild(searchWrap);

    // Filter-Pills
    this.filterRow = document.createElement("div");
    this.filterRow.className = "library-filters";
    for (const f of FILTERS) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "library-filter";
      btn.dataset.filter = f.id;
      btn.dataset.active = String(this.filter === f.id);
      btn.dataset.count = "0";
      btn.textContent = f.label;
      btn.addEventListener("click", () => {
        this.filter = f.id;
        this.renderFilterActive();
        this.renderBody();
      });
      this.filterRow.appendChild(btn);
    }
    header.appendChild(this.filterRow);

    // Zeitraum-Leiste: Alle | Heute | Woche | Monat  + ‹ Monat › Stepper
    const timeRow = document.createElement("div");
    timeRow.className = "library-filters library-timerow";
    const TIMES: ReadonlyArray<{
      id: "alle" | "heute" | "woche" | "monat";
      label: string;
    }> = [
      { id: "alle", label: "Alle" },
      { id: "heute", label: "Heute" },
      { id: "woche", label: "7 Tage" },
      { id: "monat", label: "Monat" },
    ];
    const monthNav = document.createElement("span");
    monthNav.className = "library-monthnav";
    const mPrev = document.createElement("button");
    mPrev.type = "button";
    mPrev.className = "library-month-btn";
    mPrev.textContent = "‹";
    const mLabel = document.createElement("span");
    mLabel.className = "library-month-label";
    const mNext = document.createElement("button");
    mNext.type = "button";
    mNext.className = "library-month-btn";
    mNext.textContent = "›";
    monthNav.append(mPrev, mLabel, mNext);
    const paintTime = (): void => {
      timeRow
        .querySelectorAll<HTMLButtonElement>(".library-filter")
        .forEach((b) => {
          b.dataset.active = String(b.dataset.time === this.timeFilter);
        });
      const show = this.timeFilter === "monat";
      monthNav.classList.toggle("is-hidden", !show);
      if (show) {
        const d = new Date();
        d.setDate(1);
        d.setMonth(d.getMonth() + this.monthCursor);
        mLabel.textContent = d.toLocaleDateString("de-AT", {
          month: "long",
          year: "numeric",
        });
        mNext.disabled = this.monthCursor >= 0;
      }
    };
    for (const t of TIMES) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "library-filter";
      b.dataset.time = t.id;
      b.textContent = t.label;
      b.addEventListener("click", () => {
        this.timeFilter = t.id;
        this.monthCursor = 0;
        paintTime();
        this.renderBody();
      });
      timeRow.appendChild(b);
    }
    mPrev.addEventListener("click", () => {
      this.monthCursor -= 1;
      paintTime();
      this.renderBody();
    });
    mNext.addEventListener("click", () => {
      if (this.monthCursor < 0) this.monthCursor += 1;
      paintTime();
      this.renderBody();
    });
    timeRow.appendChild(monthNav);
    paintTime();
    header.appendChild(timeRow);

    this.root.appendChild(header);

    this.body = document.createElement("div");
    this.body.className = "library-body";
    this.root.appendChild(this.body);
  }

  private renderFilterActive(): void {
    this.filterRow
      .querySelectorAll<HTMLButtonElement>(".library-filter")
      .forEach((btn) => {
        btn.dataset.active = String(btn.dataset.filter === this.filter);
      });
  }

  private renderFilterCounts(): void {
    const counts: Record<FilterKey, number> = {
      all: this.items.length,
      image: 0,
      pdf: 0,
      audio: 0,
      other: 0,
    };
    for (const i of this.items) {
      counts[mimeType(i.mime)]++;
    }
    this.filterRow
      .querySelectorAll<HTMLButtonElement>(".library-filter")
      .forEach((btn) => {
        const key = (btn.dataset.filter ?? "all") as FilterKey;
        const f = FILTERS.find((x) => x.id === key)!;
        btn.textContent = `${f.label} · ${counts[key]}`;
      });
  }

  private renderBody(errorMsg?: string): void {
    while (this.body.firstChild) this.body.removeChild(this.body.firstChild);

    if (errorMsg) {
      this.body.appendChild(emptyState(errorMsg));
      return;
    }
    if (this.items.length === 0) {
      this.body.appendChild(
        emptyState(
          "Noch keine Bilder. Hochladen oder generieren — los geht's!",
        ),
      );
      return;
    }

    const filtered = this.applyFilters();
    if (filtered.length === 0) {
      const msg = this.query
        ? `Keine Bilder zu „${this.query}".`
        : "Keine Bilder mit diesem Filter.";
      this.body.appendChild(emptyState(msg));
      return;
    }

    const groups = groupByDate(filtered);
    for (const g of groups) {
      const section = document.createElement("section");
      section.className = "library-section";
      const head = document.createElement("h3");
      head.className = "library-section-head";
      head.textContent = g.label;
      section.appendChild(head);
      if (this.viewMode === "list") {
        const list = document.createElement("div");
        list.className = "library-list";
        for (const item of g.items) list.appendChild(this.renderRow(item));
        section.appendChild(list);
      } else {
        const grid = document.createElement("div");
        grid.className = "library-grid";
        for (const item of g.items) grid.appendChild(this.renderTile(item));
        section.appendChild(grid);
      }
      this.body.appendChild(section);
    }
  }

  /** Cover-Bild (Kind-Upload) zu einem Audio-Item, falls vorhanden. */
  private coverFor(item: UploadDto): UploadDto | null {
    return (
      this.items.find(
        (u) =>
          u.parentId === item.id &&
          u.mime.startsWith("image/") &&
          (u.caption ?? "").startsWith("Cover:"),
      ) ?? null
    );
  }

  private inTimeRange(iso: string): boolean {
    if (this.timeFilter === "alle") return true;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return true;
    const now = Date.now();
    if (this.timeFilter === "heute") {
      const d0 = new Date();
      d0.setHours(0, 0, 0, 0);
      return t >= d0.getTime();
    }
    if (this.timeFilter === "woche") return t >= now - 7 * 86_400_000;
    // Monat: der per Stepper gewaehlte Kalendermonat.
    const from = new Date();
    from.setDate(1);
    from.setHours(0, 0, 0, 0);
    from.setMonth(from.getMonth() + this.monthCursor);
    const to = new Date(from);
    to.setMonth(to.getMonth() + 1);
    return t >= from.getTime() && t < to.getTime();
  }

  private applyFilters(): UploadDto[] {
    return this.items.filter((i) => {
      if (!this.inTimeRange(i.createdAt)) return false;
      // Cover-Kinder gehoeren zu ihrem Lied, nicht in die Liste.
      if (
        i.parentId &&
        i.mime.startsWith("image/") &&
        (i.caption ?? "").startsWith("Cover:")
      )
        return false;
      if (this.filter !== "all" && mimeType(i.mime) !== this.filter)
        return false;
      if (this.query) {
        const haystack = [i.caption ?? "", urlBasename(i.url), i.mime]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(this.query)) return false;
      }
      return true;
    });
  }

  // 2026-07-14 (Martin, "kann kein MP3 abspielen"): fester Player unten
  // in der Mediathek — geteilter buildAudioPlayer, ein Stueck zur Zeit.
  private playItem(item: UploadDto): void {
    this.root.querySelector(".library-player")?.remove();
    const bar = document.createElement("div");
    bar.className = "library-player";
    const title = document.createElement("span");
    title.className = "library-player-title";
    title.textContent = item.caption || urlBasename(item.url);
    const close = document.createElement("button");
    close.type = "button";
    close.className = "library-player-close";
    close.textContent = "×";
    close.setAttribute("aria-label", "Player schließen");
    close.addEventListener("click", () => bar.remove());
    const head = document.createElement("div");
    head.className = "library-player-head";
    head.append(title, close);
    // Die Abspielleiste soll sagen, WAS klingt (Martin, 05.09.).
    /*
     * 08.09.2026 — Sichern auch HIER. Martin: *„dann gleich zur Musik auch
     * einen Download-Button. In der Mediathek sollte es ja auch liegen."*
     * Es lag dort — nur ohne den Knopf, den es am Spiegel schon hatte.
     *
     * ⚠️ Kein Herz an dieser Stelle: dessen Stand muss VOR dem Bauen
     *    feststehen, und das Warten darauf verpasst den Autoplay-Tick
     *    unten, der an einer Nutzergeste haengt. Das Herz sitzt am Spiegel,
     *    wo das Stueck zu seinem Tag gehoert.
     */
    const name = item.caption?.trim() || urlBasename(item.url);
    bar.append(
      buildAudioPlayer(item.url, {
        titel: name || "Mediathek",
        sichern: { dateiname: name ? saubererDateiname(name, item.url) : urlBasename(item.url) },
      }),
    );
    bar.prepend(head);
    this.root.appendChild(bar);
    // Autoplay im User-Gesten-Tick: den PLAY-Knopf des Players druecken —
    // gezielt IM Player suchen (der erste Button der Leiste ist das X;
    // der generische Selektor schloss den Player sofort wieder — via
    // Playwright-E2E gefangen).
    bar.querySelector<HTMLButtonElement>(".alltag-player button")?.click();
  }

  private async renameItem(item: UploadDto): Promise<void> {
    const neu = window.prompt(
      "Neuer Name:",
      item.caption || urlBasename(item.url),
    );
    if (!neu?.trim()) return;
    try {
      const res = await fetch(`/api/uploads/${encodeURIComponent(item.id)}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ caption: neu.trim() }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      item.caption = neu.trim();
      this.renderBody();
    } catch (err) {
      window.alert(
        "Umbenennen fehlgeschlagen: " +
          (err instanceof Error ? err.message : String(err)) +
          " — Hinweis: braucht die neueste Server-Version.",
      );
    }
  }

  // Listen-Zeile: Icon/Thumb, Name, Datum + Groesse, Aktionen.
  private renderRow(item: UploadDto): HTMLElement {
    const row = document.createElement("div");
    row.className = "library-row";
    const kind = mimeType(item.mime);
    const thumb = document.createElement("span");
    thumb.className = "library-row-thumb";
    const cover = kind === "audio" ? this.coverFor(item) : null;
    if (kind === "image" || cover) {
      const img = document.createElement("img");
      img.loading = "lazy";
      img.src =
        "/thumb?p=" +
        encodeURIComponent((cover ?? item).url) +
        "&w=96";
      img.alt = "";
      thumb.appendChild(img);
    } else {
      thumb.appendChild(makeSvgIcon(22, typeIconPath(kind)));
    }
    const main = document.createElement("span");
    main.className = "library-row-main";
    const name = document.createElement("span");
    name.className = "library-row-name";
    name.textContent = item.caption || urlBasename(item.url);
    const meta = document.createElement("span");
    meta.className = "library-row-meta";
    const kb = item.size > 0 ? Math.round(item.size / 1024) : 0;
    meta.textContent =
      new Date(item.createdAt).toLocaleDateString("de-AT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }) +
      " · " +
      mimeShort(item.mime) +
      (kb > 0 ? ` · ${kb >= 1024 ? (kb / 1024).toFixed(1) + " MB" : kb + " KB"}` : "");
    main.append(name, meta);
    const actions = document.createElement("span");
    actions.className = "library-row-actions";
    if (kind === "audio") {
      const play = document.createElement("button");
      play.type = "button";
      play.className = "library-row-btn";
      play.title = "Abspielen";
      play.appendChild(makeSvgIcon(16, "M8 5v14l11-7z"));
      play.addEventListener("click", () => this.playItem(item));
      actions.appendChild(play);
    }
    if (kind === "audio") {
      // 2026-07-14 (Martin): Cover erzeugen/wechseln — inhaltsbezogen
      // aus dem Titel; erneuter Klick ersetzt das bestehende Cover.
      const cov = document.createElement("button");
      cov.type = "button";
      cov.className = "library-row-btn";
      cov.title = cover ? "Cover neu erzeugen" : "Cover erzeugen";
      cov.appendChild(
        makeSvgIcon(
          16,
          "M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2zM8.5 13.5l2.5 3 3.5-4.5 4.5 6H5z",
        ),
      );
      cov.addEventListener("click", () => {
        cov.disabled = true;
        cov.classList.add("is-busy");
        void fetch(`/api/uploads/${encodeURIComponent(item.id)}/cover`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: "{}",
        })
          .then((r) => r.json())
          .then((d: { ok?: boolean; error?: string }) => {
            if (!d.ok) window.alert(d.error || "Cover fehlgeschlagen");
            return this.refresh();
          })
          .catch(() => window.alert("Cover fehlgeschlagen"))
          .finally(() => {
            cov.disabled = false;
            cov.classList.remove("is-busy");
          });
      });
      actions.appendChild(cov);
    }
    const ren = document.createElement("button");
    ren.type = "button";
    ren.className = "library-row-btn";
    ren.title = "Umbenennen";
    ren.appendChild(
      makeSvgIcon(16, "M3 17.25V21h3.75L17.81 9.94l-3.75-3.75zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75z"),
    );
    ren.addEventListener("click", () => void this.renameItem(item));
    actions.appendChild(ren);
    // 2026-07-14 (Martin): Loeschen auch in der Liste (Karten hatten es).
    const del = document.createElement("button");
    del.type = "button";
    del.className = "library-row-btn library-row-btn--danger";
    del.title = "Löschen";
    del.appendChild(
      makeSvgIcon(
        16,
        "M9 3v1H4v2h1v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6h1V4h-5V3zm2 5h2v10h-2zm-4 0h2v10H7zm8 0h2v10h-2z",
      ),
    );
    del.addEventListener("click", async () => {
      const label = item.caption || urlBasename(item.url);
      if (!confirm(`„${label}" wirklich löschen?`)) return;
      try {
        const res = await fetch(`/api/uploads/${encodeURIComponent(item.id)}`, {
          method: "DELETE",
          credentials: "same-origin",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        this.items = this.items.filter((it) => it.id !== item.id);
        this.renderFilterCounts();
        this.renderBody();
      } catch (err) {
        window.alert(
          "Löschen fehlgeschlagen: " +
            (err instanceof Error ? err.message : String(err)),
        );
      }
    });
    actions.appendChild(del);
    row.append(thumb, main, actions);
    row.addEventListener("click", (ev) => {
      if ((ev.target as HTMLElement).closest("button")) return;
      if (kind === "audio") this.playItem(item);
      else if (kind === "image") this.openLightbox(item);
      else window.open(item.url, "_blank");
    });
    return row;
  }

  private renderTile(item: UploadDto): HTMLElement {
    const tile = document.createElement("button");
    tile.type = "button";
    tile.className = "library-tile";
    tile.dataset.type = mimeType(item.mime);
    const filename = urlBasename(item.url);
    tile.title = item.caption ?? filename;

    if (item.mime.startsWith("image/")) {
      const img = document.createElement("img");
      // 2026-07-14 (Martin, "Bilder laden so langsam"): 320px-Thumbnail
      // vom /thumb-Endpunkt statt Original (teils MB-gross) + lazy.
      img.loading = "lazy";
      img.decoding = "async";
      img.src = "/thumb?p=" + encodeURIComponent(item.url) + "&w=320";
      img.alt = item.caption ?? "";
      img.loading = "lazy";
      tile.appendChild(img);
    } else {
      // Pre.157: Non-Image-Tile = Icon + Filename
      const iconBox = document.createElement("span");
      iconBox.className = "library-tile-icon";
      iconBox.appendChild(makeSvgIcon(36, typeIconPath(mimeType(item.mime))));
      tile.appendChild(iconBox);

      const name = document.createElement("span");
      name.className = "library-tile-filename";
      name.textContent = item.caption || filename;
      tile.appendChild(name);
    }

    if (item.source !== "user") {
      const tag = document.createElement("span");
      tag.className = "library-tile-source";
      tag.dataset.source = item.source;
      // 16.10.2026: Bilder des Schmieds (Dreier-Chat) sind kein KI-Bild.
      tag.textContent = item.source === "generated" ? "KI" : item.source === "schmied" ? "Schmied" : "Edit";
      tile.appendChild(tag);
    }

    const fmt = document.createElement("span");
    fmt.className = "library-tile-format";
    fmt.textContent = mimeShort(item.mime);
    tile.appendChild(fmt);

    if (item.caption && item.mime.startsWith("image/")) {
      const caption = document.createElement("span");
      caption.className = "library-tile-caption";
      caption.textContent = item.caption;
      tile.appendChild(caption);
    }

    // Pre.158: Teilen-Button rechts oben (Hover-only) für jedes Tile.
    const share = document.createElement("button");
    share.type = "button";
    share.className = "library-tile-share";
    share.setAttribute("aria-label", "Teilen");
    share.title = "Teilen";
    share.appendChild(
      makeSvgIcon(
        14,
        "M18 16.08a3 3 0 0 0-2.4 1.2L8.91 14.4a3.36 3.36 0 0 0 0-1.46l6.61-3.85a3 3 0 1 0-1-2.27 3.4 3.4 0 0 0 0 .54L7.91 10.92a3 3 0 1 0 0 2.16l6.62 3.86a3 3 0 0 0-.06.55 2.94 2.94 0 1 0 2.94-2.94z",
      ),
    );
    share.addEventListener("click", async (ev) => {
      ev.stopPropagation();
      const { shareMedia } = await import("./share.js");
      await shareMedia({
        url: item.url,
        title: item.caption ?? urlBasename(item.url),
        text: item.caption ?? "",
      });
    });
    tile.appendChild(share);

    // Pre.804 (#72a): Medium an Projekt hängen — nutzt die seit jeher
    // existierenden project_links (33 lagen unsichtbar in der DB).
    const toProj = document.createElement("button");
    toProj.type = "button";
    toProj.className = "library-tile-project";
    toProj.setAttribute("aria-label", "Zu Projekt");
    toProj.title = "Zu Projekt";
    toProj.appendChild(
      makeSvgIcon(
        14,
        "M10 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-8l-2-2zm3 7h2v3h3v2h-3v3h-2v-3h-3v-2h3z",
      ),
    );
    toProj.addEventListener("click", async (ev) => {
      ev.stopPropagation();
      const [{ listProjects, linkToProject }, { selectSheet }] =
        await Promise.all([
          import("./projects-api.js"),
          import("./sheet-ui.js"),
        ]);
      const projects = await listProjects().catch(() => []);
      if (projects.length === 0) {
        window.alert("Noch keine Projekte vorhanden.");
        return;
      }
      const choice = await selectSheet({
        title: "Zu welchem Projekt?",
        options: projects.map((pr) => ({ value: pr.id, label: pr.name })),
      });
      if (!choice) return;
      try {
        await linkToProject(choice, "upload", item.id);
      } catch (err) {
        window.alert(
          `Verknüpfen fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
    });
    tile.appendChild(toProj);

    // Pre.316: Lösch-Button (links oben) — DELETE /api/uploads/:id
    const del = document.createElement("button");
    del.type = "button";
    del.className = "library-tile-delete";
    del.setAttribute("aria-label", "Löschen");
    del.title = "Löschen";
    del.appendChild(
      makeSvgIcon(
        14,
        "M9 3v1H4v2h1v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6h1V4h-5V3zm2 5h2v10h-2zm-4 0h2v10H7zm8 0h2v10h-2z",
      ),
    );
    del.addEventListener("click", async (ev) => {
      ev.stopPropagation();
      const label = item.caption || urlBasename(item.url);
      if (!confirm(`„${label}" wirklich löschen?`)) return;
      try {
        const res = await fetch(`/api/uploads/${encodeURIComponent(item.id)}`, {
          method: "DELETE",
          credentials: "same-origin",
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        // Lokal aus Liste entfernen, neu rendern
        this.items = this.items.filter((it) => it.id !== item.id);
        this.renderFilterCounts();
        this.renderBody();
      } catch (err) {
        alert(
          "Löschen fehlgeschlagen: " +
            (err instanceof Error ? err.message : String(err)),
        );
      }
    });
    tile.appendChild(del);

    tile.addEventListener("click", () => this.openItem(item));
    return tile;
  }

  /** Pre.157: Bilder → Lightbox; 2026-07-14: Audio → Player unten;
   *  andere → neuer Tab. */
  private openItem(item: UploadDto): void {
    if (item.mime.startsWith("image/")) {
      this.openLightbox(item);
    } else if (mimeType(item.mime) === "audio") {
      this.playItem(item);
    } else {
      window.open(item.url, "_blank", "noopener,noreferrer");
    }
  }

  // --- Lightbox-Galerie -----------------------------------------------
  // Lightbox-Logik (Pfeiltasten, Swipe, Counter) lebt in lib/lightbox.ts.
  // Die Library übergibt die aktuell gefilterte Liste + Start-Index.

  private openLightbox(item: UploadDto): void {
    const filtered = this.applyFilters();
    const idx = Math.max(
      0,
      filtered.findIndex((i) => i.id === item.id),
    );
    void import("./lightbox.js").then(({ openGallery }) => {
      openGallery(filtered, idx, {
        onAttach: (it) => {
          const full = filtered.find((x) => x.id === it.id);
          if (full) this.opts.onAttach?.(full);
        },
      });
    });
  }

  private bindLightboxClose(): void {
    /* no-op — Close-Logik liegt jetzt im Lightbox-Modul. */
  }
}

// --- Helpers -----------------------------------------------------------

function emptyState(msg: string): HTMLElement {
  const el = document.createElement("div");
  el.className = "library-empty";
  el.textContent = msg;
  return el;
}

function makeSvgIcon(size: number, pathD: string): SVGElement {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", String(size));
  svg.setAttribute("height", String(size));
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS(ns, "path");
  path.setAttribute("fill", "currentColor");
  path.setAttribute("d", pathD);
  svg.appendChild(path);
  return svg;
}

function mimeShort(mime: string): string {
  const sub = mime.split("/")[1] ?? "";
  if (sub === "jpeg") return "JPG";
  return sub.toUpperCase().slice(0, 4);
}

function urlBasename(url: string): string {
  try {
    const path = url.split("?")[0] ?? url;
    const last = path.split("/").pop() ?? path;
    return decodeURIComponent(last);
  } catch {
    return url;
  }
}

/**
 * Ein Dateiname, den man in „Dateien" wiederfindet.
 *
 * ⚠️ Ohne ihn heisst die gesicherte Datei `9b97870b-6a8c-….mp3` — der
 *    Unterschied zwischen einer Sammlung und einem Haufen. Der Titel aus der
 *    Mediathek traegt schon alles Noetige („Essenz-Musik 2026-09-07 · Lyria"),
 *    er muss nur dateitauglich werden.
 */
export function saubererDateiname(titel: string, url: string): string {
  const endung = url.split(".").pop()?.split("?")[0] || "dat";
  const rein = titel
    .replace(/[\\/:*?"<>|]/g, "-")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 80);
  return `${rein || "Mediathek"}.${endung}`;
}

function typeIconPath(t: FilterKey): string {
  // Vereinfachte Icons als SVG-Path-d.
  if (t === "pdf") {
    return "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm-3 13H7.5v-2H10v2zM10 12H7.5v-2H10v2zm6 3h-1.5v-2H16v2zm0-3h-1.5v-2H16v2zm-3-4V3.5L18.5 9z";
  }
  if (t === "audio") {
    return "M12 3v10.55a4 4 0 1 0 2 3.45V7h4V3z";
  }
  // other / generic file
  return "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm0 7V3.5L18.5 9z";
}

interface DateGroup {
  label: string;
  items: UploadDto[];
}

function groupByDate(items: UploadDto[]): DateGroup[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(today.getDate() - 7);

  const buckets = new Map<string, DateGroup>();
  for (const item of items) {
    const d = new Date(item.createdAt);
    let label: string;
    let sortKey: string;
    if (d >= today) {
      label = "Heute";
      sortKey = "0_today";
    } else if (d >= yesterday) {
      label = "Gestern";
      sortKey = "1_yesterday";
    } else if (d >= weekAgo) {
      label = "Diese Woche";
      sortKey = "2_week";
    } else {
      label = d.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
      // Älteste Datums-Buckets nach createdAt absteigend gruppieren —
      // sortKey enthält das Datum invertiert, damit neuer = höher.
      sortKey = `9_${d.toISOString().slice(0, 10)}`;
    }
    let bucket = buckets.get(sortKey);
    if (!bucket) {
      bucket = { label, items: [] };
      buckets.set(sortKey, bucket);
    }
    bucket.items.push(item);
  }
  // Sortiere Buckets: "Heute" → "Gestern" → "Diese Woche" → ältere
  // Datums-Buckets nach Datum absteigend.
  const keys = [...buckets.keys()].sort((a, b) => {
    if (a.startsWith("9_") && b.startsWith("9_")) return b.localeCompare(a);
    return a.localeCompare(b);
  });
  return keys.map((k) => buckets.get(k)!);
}
